/*
 * Copyright (c) 2024 Paul Ostrowski. All rights reserved.
 *
 * This software is the proprietary information of Paul Ostrowski.
 * Unauthorized copying, modification, or distribution of this code, via any medium, is strictly prohibited.
 * This code is proprietary and confidential.
 *
 * For licensing inquiries, contact Paul Ostrowski at postrowski@gmail.com.
*/

import React, { useState, useEffect, useRef } from 'react';
import './Tour.css';
import PreferenceService from '../services/PreferenceService';

export interface TourStep {
  highlightElement: string;
  alternateElement?: string;
  positionNextToElement?: string;
  scrollIntoView: boolean;
  title: string;
  description: JSX.Element;
  testToContinue?: () => boolean;
  position: 'top' | 'bottom' | 'left' | 'right' | 'center';
}

interface TourProps {
  steps: TourStep[];
  currentStepIndex: number;
  setCurrentStepIndex: (step: number) => void;
  onClose: () => void;
}

const Tour: React.FC<TourProps> = ({ steps, currentStepIndex, setCurrentStepIndex, onClose }) => {
  const [highlightRect, setHighlightRect] = useState<{top: number, left: number, width: number, height:number}>({top:0, left: 0, width: 0, height: 0});
  const [dialogLocation, setDialogLocation] = useState<{top: number, left: number}>({top: 0, left: 0});
  const [isClosing, setIsClosing] = useState<boolean>(false);

  const previousEnabledRef = useRef<Element | null>(null);
  
  const closeStep: TourStep = {
    highlightElement: '#startTour',
    title: 'Resume Tour',
    scrollIntoView: true,
    description: (<div>If you want to restart this tour, you can click on this button at anytime.</div>),
    position: 'bottom'
  };

  let currentStep: TourStep = isClosing ? closeStep : steps[currentStepIndex];

  const onCloseClick = () => {
    if (isClosing) {
      onClose();
    } else {
      setIsClosing(true)
    }
  }
  const nextStep = () => {
    const nextStepIndex = currentStepIndex + 1;
    if ((nextStepIndex >= 0) && (nextStepIndex < steps.length)) {
      setCurrentStepIndex(nextStepIndex);
    }
  }

  if (currentStep?.testToContinue && currentStep.testToContinue() && !isClosing) {
    // once the current step's test is complete (it returned 'true'), move on the next step.
    // But do it in a timeout since we are in the render cycle, and setting the currentStepIndex
    // is a change to the parent control.
    setTimeout(() => nextStep(), 10);
  }

  const updateHighlightPosition = () => {
    if (currentStep && currentStep.highlightElement) {
      let highlightElement: Element | null | undefined = document.querySelector(currentStep.highlightElement);
      if (!highlightElement && currentStep.alternateElement) {
        // if they closed the focus element, put focus on the alternateElement
        highlightElement = document.querySelector(currentStep.alternateElement);
      }
      if (highlightElement) {
        const rect:DOMRect = highlightElement.getBoundingClientRect();

        // Ensure the target element is focusable and interactable
        highlightElement!.classList.add('tour-highlighted');

        if (highlightRect.top != rect.top || highlightRect.left != rect.left ||
            highlightRect.width != rect.width || highlightRect.height != rect.height) {
          setHighlightRect(rect);
        }

        const dialogElement: Element | null = document.querySelector('#TourDialog');
        let positionNextToElement: Element | null | undefined = currentStep.positionNextToElement ? document.querySelector(currentStep.positionNextToElement) || highlightElement : highlightElement;
        if (dialogElement && positionNextToElement) {
          positionElementNextTo(dialogElement, positionNextToElement, currentStep.position);
        }
      }
    }
  };

  const positionElementNextTo = (mobileElement: Element, fixedElement: Element, 
                                 direction: 'top'|'bottom'|'left'|'right'|'center') => {
    // Get the bounding rectangles of both elements
    const rect1 = mobileElement.getBoundingClientRect();
    const rect2 = fixedElement.getBoundingClientRect();
  
    // Calculate the new position for element1
    let top = 0;
    let left = 0;
  
    switch (direction) {
      case 'top':
        top = rect2.top - rect1.height - 10;
        left = rect2.left + rect2.width / 2 - rect1.width / 2;
        break;
      case 'bottom':
        top = rect2.bottom + 10;
        left = rect2.left + rect2.width / 2 - rect1.width / 2;
        break;
      case 'left':
        top = rect2.top + rect2.height / 2 - rect1.height / 2;
        left = rect2.left - rect1.width - 10;
        break;
      case 'right':
        top = rect2.top + rect2.height / 2 - rect1.height / 2;
        left = rect2.right + 10;
        break;
      case 'center':
        top = rect2.top + rect2.height / 2 - rect1.height / 2;
        left = rect2.left + rect2.width / 2 - rect1.width / 2;
        break;
    }
  
    // Apply the calculated position to element1
    if (dialogLocation.left != left || dialogLocation.top != top) {
      setDialogLocation({top, left});
    }
  }
  
  useEffect(() => {
    if (currentStep && currentStep.highlightElement) {
      // Scroll the current step into view
      if (currentStep.scrollIntoView !== false) {
        const targetElement = document.querySelector(currentStep.highlightElement);
        targetElement?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }

      // Remove highlight class from the previous element
      if (previousEnabledRef.current) {
        previousEnabledRef.current.classList.remove('tour-highlighted');
      }

      // Update position and store the currently highlighted element
      updateHighlightPosition();
      previousEnabledRef.current = document.querySelector(currentStep.highlightElement);
    }

    const handleKeyDown = (event: KeyboardEvent) => {
      // if (event.key === 'Escape') {
      //   onClose();
      // }
    };

    window.addEventListener('scroll', updateHighlightPosition);
    window.addEventListener('resize', updateHighlightPosition);
    window.addEventListener('keydown', handleKeyDown);

    return () => {
      window.removeEventListener('scroll', updateHighlightPosition);
      window.removeEventListener('resize', updateHighlightPosition);
      window.removeEventListener('keydown', handleKeyDown);

      // Clean up highlight class when the tour closes or changes steps
      if (previousEnabledRef.current) {
        previousEnabledRef.current.classList.remove('tour-highlighted');
      }
    };
  }, [currentStep]);

  const lastStep = currentStepIndex === steps.length - 1;
  const hideNext = lastStep || (currentStep.testToContinue !== undefined);
  return currentStep ? (
    <div className="tour-overlay">
      <div className="highlight-area" style={{top: highlightRect.top+'px', left: highlightRect.left+'px', width: highlightRect.width+'px', height: highlightRect.height+'px'}}></div>
      <div id='TourDialog'
        className="tour-dialog"
        style={{position: 'absolute', top: dialogLocation.top+'px', left: dialogLocation.left+'px'}}
        role="dialog"
        aria-labelledby="tour-title"
        aria-describedby="tour-description"
      >
        <h3 id="tour-title" style={{margin: '0px'}}>{currentStep.title}</h3>
        <hr/>
        <span id="tour-description">{currentStep.description}</span>
        { isClosing && (
          <div className="tour-controls">
            <button onClick={onClose}>Got it!</button>
          </div>)}
        { !isClosing && (<>
              <div className="tour-controls">
                <button style={{opacity: 0}}></button>
                <button onClick={nextStep} disabled={hideNext} style={{opacity: (hideNext ? 0 : 1)}}>Next</button>
                {lastStep && <button onClick={onCloseClick}>Done!</button>}
              </div>
              {!lastStep && <a style={{position: 'absolute', right: '10px', bottom: '10px', fontSize: 'smaller'}} href="#" onClick={onCloseClick}>Close Tour</a>}
            </>)}
      </div>
    </div>
  ) : null;
};

export default Tour;
